import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Link,
  Dialog,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

import { BrowserTitle, Spinner } from '../components';
import lowPriceRequestsServices from '../services/lowPriceRequestsServices';
import { placeAutoOrdersForProposal } from '../utils/autoOrderService';
import { moment, formatDateTime } from '../utils/dateUtils';
import { Property } from './Property';

const STATUS_COLORS = {
  Pending: 'warning',
  Approved: 'success',
  Declined: 'error',
};

const money = (value) =>
  `$${(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

// Review queue (and history) of contracts blocked from scheduling because
// their value is below the configured low-price threshold. Visible to users
// flagged as Emergency contact; Approve puts the job on the schedule with the
// requested dates/resources, Decline notifies the requester.
export const LowPriceRequests = () => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [viewProposalId, setViewProposalId] = useState(null);
  const [viewPropertyId, setViewPropertyId] = useState(null);
  const [actingId, setActingId] = useState(null);

  const loadRequests = useCallback(async (status) => {
    setLoading(true);
    try {
      const response = await lowPriceRequestsServices.getAll(status === 'All' ? undefined : status);
      setRequests(response.data || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error('Access denied: only Emergency contact users can view this page');
      } else {
        toast.error('Failed to load requests');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests(statusFilter);
  }, [statusFilter, loadRequests]);

  const handleApprove = (request) => {
    confirm({
      title: 'Approve low-price contract?',
      description: `Contract ${request.proposal?.publicId} (${money(
        request.contractValue,
      )}) will be added to the schedule with the requested dates and resources.`,
      confirmationText: 'Approve',
    })
      .then(async () => {
        setActingId(request.id);
        try {
          await lowPriceRequestsServices.approve(request.id);
          toast.success(`${request.proposal?.publicId} approved and added to the schedule`);
          loadRequests(statusFilter);

          // Approval confirms the job — order the equipment automatically,
          // no manual review step. Ordering problems never undo the approval.
          try {
            const result = await placeAutoOrdersForProposal({
              proposalId: request.proposal?.id,
              deliveryDates: (request.schedules || []).map((s) => s.date),
              autoNote: 'This order was placed automatically when the low-price request was approved.',
            });
            if (result.placed > 0) {
              toast.success(
                `Equipment order sent automatically to ${result.placed} supplier${result.placed !== 1 ? 's' : ''}` +
                (result.willCall ? ' (WILL CALL / PREPULL — job is today/tomorrow)' : ''),
              );
            } else if (result.allCovered) {
              toast.info('Equipment was already ordered for this contract.');
            } else if (!result.nothingToOrder) {
              toast.warning('Equipment was not auto-ordered — please order manually from the proposal page.');
            }
            if (result.unresolved.length > 0) {
              toast.warning(
                `Not auto-ordered (no supplier in catalog): ${result.unresolved.join(', ')}. Order manually.`,
                { autoClose: 12000 },
              );
            }
          } catch (orderError) {
            console.error('Auto-order after approval failed:', orderError);
            toast.warning('Approved, but automatic equipment ordering failed — order manually from the proposal page.');
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to approve request');
          console.error(error);
        } finally {
          setActingId(null);
        }
      })
      .catch(() => {});
  };

  const handleDecline = (request) => {
    confirm({
      title: 'Decline low-price contract?',
      description: `Contract ${request.proposal?.publicId} will NOT be added to the schedule. The requester will be notified by email.`,
      confirmationText: 'Decline',
    })
      .then(async () => {
        setActingId(request.id);
        try {
          await lowPriceRequestsServices.decline(request.id);
          toast.success(`${request.proposal?.publicId} declined, requester notified`);
          loadRequests(statusFilter);
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to decline request');
          console.error(error);
        } finally {
          setActingId(null);
        }
      })
      .catch(() => {});
  };

  return (
    <Box component="section" id="low-price-requests-page">
      <BrowserTitle title="Low-Price Contract Requests | Airmaxx Pro" />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Low-Price Contract Requests
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size="small"
            onChange={(e, value) => value && setStatusFilter(value)}
          >
            <ToggleButton value="Pending">Pending</ToggleButton>
            <ToggleButton value="Approved">Approved</ToggleButton>
            <ToggleButton value="Declined">Declined</ToggleButton>
            <ToggleButton value="All">All</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => loadRequests(statusFilter)} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Spinner />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Value / Minimum</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested installation</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested by</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No requests
                  </TableCell>
                </TableRow>
              )}
              {requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Link
                      component="button"
                      underline="hover"
                      sx={{ fontWeight: 600 }}
                      onClick={() => setViewProposalId(request.proposal?.id)}
                    >
                      {request.proposal?.publicId}
                    </Link>
                    {request.proposal?.isWarranty && (
                      <Chip label="Warranty" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {request.proposal?.propertyId ? (
                      <Link
                        component="button"
                        underline="hover"
                        sx={{ textAlign: 'left' }}
                        onClick={() => setViewPropertyId(request.proposal.propertyId)}
                      >
                        {request.proposal?.propertyAddress || '—'}
                      </Link>
                    ) : (
                      request.proposal?.propertyAddress || '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ color: '#c62828', fontWeight: 600, fontSize: 'inherit' }}>
                      {money(request.contractValue)}
                    </Typography>
                    {' / '}
                    {money(request.threshold)}
                  </TableCell>
                  <TableCell>
                    {(request.schedules || []).map((s, i) => (
                      <Typography key={`${s.date}-${i}`} variant="body2">
                        {moment(s.date, 'YYYY-MM-DD').format('MM/DD/YYYY')} — {s.installationTime} —{' '}
                        {s.installersCount} installers, {s.trucksCount} trucks
                      </Typography>
                    ))}
                    {request.note && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        Note: {request.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{request.requestedBy?.fullName || '—'}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {formatDateTime(request.createdAt, 'L, h:mm a')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status}
                      color={STATUS_COLORS[request.status] || 'default'}
                      size="small"
                    />
                    {request.decidedBy && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        by {request.decidedBy.fullName}
                        {request.decidedAt ? ` (${formatDateTime(request.decidedAt, 'L, h:mm a')})` : ''}
                      </Typography>
                    )}
                    {request.smsAcknowledgedAt ? (
                      <Tooltip title={request.smsAcknowledgedBy || ''}>
                        <Typography variant="body2" sx={{ color: 'success.main', mt: 0.5 }}>
                          SMS confirmed by {(request.smsAcknowledgedBy || 'unknown').split(' (')[0]}
                          {` (${formatDateTime(request.smsAcknowledgedAt, 'L, h:mm a')})`}
                        </Typography>
                      </Tooltip>
                    ) : request.smsAttempts > 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        SMS sent x{request.smsAttempts}, no reply yet
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="center">
                    {request.status === 'Pending' ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckIcon />}
                          disabled={actingId === request.id}
                          onClick={() => handleApprove(request)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CloseOutlinedIcon />}
                          disabled={actingId === request.id}
                          onClick={() => handleDecline(request)}
                        >
                          Decline
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip title="Already decided">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          —
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Proposal preview modal (same view as /proposals/view/:id) */}
      <Dialog
        open={Boolean(viewProposalId)}
        onClose={() => setViewProposalId(null)}
        maxWidth="xl"
        fullWidth
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
          <IconButton onClick={() => setViewProposalId(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, height: '85vh' }}>
          {viewProposalId && (
            <iframe
              src={`/proposals/view/${viewProposalId}`}
              title="Proposal view"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Property preview modal (same view as /properties/:id, sans Back button) */}
      <Dialog
        open={Boolean(viewPropertyId)}
        onClose={() => setViewPropertyId(null)}
        maxWidth="xl"
        fullWidth
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
          <IconButton onClick={() => setViewPropertyId(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0, height: '85vh' }}>
          {viewPropertyId && <Property propertyId={viewPropertyId} embedded />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LowPriceRequests;
